/**
 * useMonadWallet.ts
 * ─────────────────────────────────────────────────────────────
 * Handles Rabby (EIP-1193) wallet connection and Monad Testnet
 * network management.
 *
 * Monad Testnet specs:
 *   Chain ID : 10143  (0x279F)
 *   RPC      : https://testnet-rpc.monad.xyz
 *   Explorer : https://testnet.monadexplorer.com
 * ─────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback } from "react";

// ─── Monad Testnet chain config ────────────────────────────────

export const MONAD_MAINNET = {
  chainId: "0x8F",
  chainName: "Monad Mainnet",
  rpcUrls: ["https://rpc.monad.xyz"],
  blockExplorerUrls: ["https://monadvision.com"],
  nativeCurrency: { name: "MON", symbol: "MON", decimals: 18 },
}
export const MONAD_CHAIN_ID_DEC = 143

// ─── types ─────────────────────────────────────────────────────

export interface WalletState {
  address: string | null;
  chainId: number | null;
  isConnected: boolean;
  isConnecting: boolean;
  isWrongNetwork: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchToMonad: () => Promise<void>;
}

// ─── helper: get injected provider ────────────────────────────

function getProvider(): any {
  if (typeof window === "undefined") return null;
  // Rabby injects as window.ethereum (EIP-1193)
  return (window as any).ethereum ?? null;
}

// ─── hook ──────────────────────────────────────────────────────

export function useMonadWallet(): WalletState {
  const [address, setAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  const isConnected = Boolean(address);
  const isWrongNetwork = isConnected && chainId !== MONAD_CHAIN_ID_DEC;

  // ── restore session on mount ──────────────────────────────

  useEffect(() => {
    const provider = getProvider();
    if (!provider) return;

    // Silently check if already connected (no prompt)
    provider
      .request({ method: "eth_accounts" })
      .then((accounts: string[]) => {
        if (accounts.length > 0) {
          setAddress(accounts[0].toLowerCase());
        }
      })
      .catch(() => {});

    provider
      .request({ method: "eth_chainId" })
      .then((hex: string) => setChainId(parseInt(hex, 16)))
      .catch(() => {});
  }, []);

  // ── listen to wallet events ───────────────────────────────

  useEffect(() => {
    const provider = getProvider();
    if (!provider) return;

    const onAccounts = (accounts: string[]) => {
      if (accounts.length === 0) {
        setAddress(null);
        setChainId(null);
      } else {
        setAddress(accounts[0].toLowerCase());
      }
    };

    const onChain = (hex: string) => {
      setChainId(parseInt(hex, 16));
    };

    provider.on("accountsChanged", onAccounts);
    provider.on("chainChanged", onChain);

    return () => {
      provider.removeListener("accountsChanged", onAccounts);
      provider.removeListener("chainChanged", onChain);
    };
  }, []);

  // ── connect ───────────────────────────────────────────────

  const connect = useCallback(async () => {
    const provider = getProvider();
    if (!provider) {
      alert(
        "No wallet detected. Please install Rabby Wallet and refresh the page."
      );
      return;
    }

    setIsConnecting(true);
    try {
      const accounts: string[] = await provider.request({
        method: "eth_requestAccounts",
      });
      if (accounts.length > 0) {
        setAddress(accounts[0].toLowerCase());
      }

      const hex: string = await provider.request({ method: "eth_chainId" });
      setChainId(parseInt(hex, 16));
    } catch (err: any) {
      console.error("Wallet connect failed:", err);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  // ── disconnect (clear local state only) ───────────────────

  const disconnect = useCallback(() => {
    setAddress(null);
    setChainId(null);
  }, []);

  // ── switch to Monad Testnet ───────────────────────────────

  const switchToMonad = useCallback(async () => {
    const provider = getProvider();
    if (!provider) return;

    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [MONAD_MAINNET],
      });
    } catch (switchErr: any) {
      // 4902 = chain not added yet
      if (switchErr.code === 4902) {
        try {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [MONAD_MAINNET],
          });
        } catch (addErr) {
          console.error("Failed to add Monad Mainnet:", addErr);
        }
      } else {
        console.error("Failed to switch network:", switchErr);
      }
    }
  }, []);

  return {
    address,
    chainId,
    isConnected,
    isConnecting,
    isWrongNetwork,
    connect,
    disconnect,
    switchToMonad,
  };
}
